import { OptionType } from '@atlaskit/select';
import debounce from 'lodash/debounce';
import * as React from 'react';
import compact from 'lodash/compact';

import useErrorController from '@/components/primitive/error-boundary/hooks';
import meta, { AutocompletableType } from '@/meta';
import { get } from '@/utils';

// we can't use a raw lodash/debounced function because it will return a stale
// promise (or even undefined if leading=false and it is the very first
// invocation), so we wrap it in a method which always synchronously returns a
// Promise
export function useBetterDebounce<T, U>(
  // a function which takes an argument and does something with it, then invokes
  // its second argument (the callback) with the result
  f: (a: T, callback: (c: U) => void) => void,
  unresolvedValue: U,
  deps: React.DependencyList
): (a: T) => Promise<U> {
  const debouncedGet = React.useCallback(debounce(f, 500, { trailing: true, leading: true }), deps);

  const oldRes = React.useRef<undefined | ((c: U) => void)>(undefined);
  return React.useCallback(
    (a: T) => {
      if (oldRes.current !== undefined) {
        // it's okay if this already resolved as subsequent calls to res are noops
        // pass in [] because the result gets thrown away anyway
        oldRes.current(unresolvedValue);
      }
      return new Promise((res: (c: U) => void) => {
        // resolve old promises since @atlaskit/field waits on them indefinitely
        // https://ecosystem.atlassian.net/jira/servicedesk/projects/DS/issue/DS-7424
        oldRes.current = res;
        debouncedGet(a, res);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unresolvedValue, debouncedGet]
  );
}

type AutocompleteOptionsType = {
  // use "true" when the API returns an array of strings instead of objects
  formatPlainOption?: boolean;
  // the key to use for assigning the value of the option (defaults to publicId)
  // e.g use "orgId" for orgs since the API returns an array of objects doesn't
  // have a publicId
  optionValuekey?: string;
  // typePublicId is used to fetch the autocomplete url by type id (e.g. sloGroupPublicId)
  typePublicId?: string;
  // params to pass to the appended part of the search query (e.g. `?...&level=foo`)
  params?: string;
  // whether to use the mimir endpoint
  mimir?: boolean;
  // whether to use the dynamic cohort names endpoint
  dynamic?: boolean;
  // keep empty options
  keepEmptyOptions?: boolean;
};

function useAutoCompleteController(
  type: AutocompletableType,
  options: AutocompleteOptionsType = {}
): (s: string | undefined) => Promise<(OptionType & Partial<API.Entity>)[]> {
  const setGlobalError = useErrorController();
  return useBetterDebounce<string | undefined, (OptionType & Partial<API.Entity>)[]>(
    (s: string | undefined, callback: (c: (OptionType & Partial<API.Entity>)[]) => void) => {
      const sEnc = s === undefined ? '' : encodeURIComponent(s);
      if (!meta[type]) return;
      const endpointPath = options.typePublicId
        ? (meta[type].endpoints as any).autocomplete(
            options.typePublicId,
            options.mimir,
            options.dynamic
          )
        : meta[type].endpoints.autocomplete;
      const params = options.params || '';
      get<API.SearchResponse>(
        // eslint-disable-next-line no-nested-ternary
        `${endpointPath}${
          // eslint-disable-next-line no-nested-ternary
          s ? `?name=${sEnc}${params ? `&${params}` : ''}` : params ? `?${params}` : ''
        }`
      )
        .then(res => {
          if (options.formatPlainOption) {
            callback(
              options.keepEmptyOptions
                ? res.map((option: any) => ({ value: option, label: option }))
                : compact(res).map((option: any) => ({
                    value: option,
                    label: option,
                  }))
            );
            return;
          }

          callback(
            res.map((option: any) => ({
              ...option,
              value: option[options.optionValuekey || 'publicId'],
              label: option.name || option.checkName,
            }))
          );
        })
        .catch((e: API.Error) => {
          setGlobalError({
            title: `Search failed (${e.status || 500})`,
            message: `[${e.name || 'Unknown Error'}] ${e.message}`,
          });
          callback([]);
        });
    },
    [],
    [type]
  );
}

export default useAutoCompleteController;
